import {
  Ban,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  Download,
  Loader2,
  MailWarning,
  Pencil,
  Receipt,
  Send,
  Undo2,
} from 'lucide-react'

import type { Invoice } from '@/shared/domain'
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/primitives'

import { canExtendDueDate } from '@/shared/domain'

export type PaymentRowActionsProps = {
  invoice: Invoice
  isExpanded: boolean
  sendingId: string | null
  resendingId: string | null
  downloadingId: string | null
  viewingReceiptId: string | null
  editingId: string | null
  markingAsPaidId: string | null
  onSend: (invoice: Invoice) => void
  onResend: (invoice: Invoice) => void
  onDownloadPdf: (invoice: Invoice) => void
  onViewReceipt: (invoice: Invoice) => void
  onRequestVoid: (invoice: Invoice) => void
  onRequestRefund: (invoice: Invoice) => void
  onRequestEdit: (invoice: Invoice) => void
  onRequestExtend: (invoice: Invoice) => void
  onMarkAsPaid: (invoice: Invoice) => void
  onToggleExpand: (id: string) => void
}

export function PaymentRowActions({
  invoice,
  isExpanded,
  sendingId,
  resendingId,
  downloadingId,
  viewingReceiptId,
  editingId,
  markingAsPaidId,
  onSend,
  onResend,
  onDownloadPdf,
  onViewReceipt,
  onRequestVoid,
  onRequestRefund,
  onRequestEdit,
  onRequestExtend,
  onMarkAsPaid,
  onToggleExpand,
}: PaymentRowActionsProps) {
  const canEdit = invoice.availableActions.includes('edit')
  const canExtend = canExtendDueDate(invoice)
  const canSend = invoice.availableActions.includes('send')
  const canResend = invoice.availableActions.includes('resend')
  const canDownloadPdf = invoice.status !== 'draft'
  const canViewReceipt =
    Boolean(invoice.providerInvoiceId) && invoice.status === 'paid'
  const canVoid = invoice.availableActions.includes('void')
  const canRefund = invoice.availableActions.includes('refund')
  const canMarkAsPaid = invoice.availableActions.includes('mark_paid')
  const isScheduled = invoice.status === 'scheduled'

  const isSending = sendingId === invoice.id
  const isResending = resendingId === invoice.id
  const isDownloading = downloadingId === invoice.id
  const isViewingReceipt = viewingReceiptId === invoice.id
  const isEditing = editingId === invoice.id
  const isMarkingAsPaid = markingAsPaidId === invoice.id

  return (
    <div className="flex items-center justify-end gap-1">
      <TooltipProvider>
        {canEdit ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Edit invoice"
                disabled={isEditing}
                onClick={(e) => {
                  e.stopPropagation()
                  onRequestEdit(invoice)
                }}
              >
                <Pencil className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit invoice</TooltipContent>
          </Tooltip>
        ) : null}

        {canExtend ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Change due date"
                onClick={(e) => {
                  e.stopPropagation()
                  onRequestExtend(invoice)
                }}
              >
                <CalendarClock className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Change due date</TooltipContent>
          </Tooltip>
        ) : null}

        {canSend ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Send invoice"
                disabled={isSending}
                onClick={(e) => {
                  e.stopPropagation()
                  onSend(invoice)
                }}
              >
                {isSending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Send invoice</TooltipContent>
          </Tooltip>
        ) : null}

        {canResend ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Resend invoice email"
                disabled={isResending}
                onClick={(e) => {
                  e.stopPropagation()
                  onResend(invoice)
                }}
              >
                {isResending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <MailWarning className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Resend email</TooltipContent>
          </Tooltip>
        ) : null}

        {canDownloadPdf ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Download PDF"
                disabled={isDownloading}
                onClick={(e) => {
                  e.stopPropagation()
                  onDownloadPdf(invoice)
                }}
              >
                {isDownloading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Download className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Download PDF</TooltipContent>
          </Tooltip>
        ) : null}

        {canViewReceipt ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="View receipt"
                disabled={isViewingReceipt}
                onClick={(e) => {
                  e.stopPropagation()
                  onViewReceipt(invoice)
                }}
              >
                {isViewingReceipt ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Receipt className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>View receipt</TooltipContent>
          </Tooltip>
        ) : null}

        {canMarkAsPaid ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Log payment"
                disabled={isMarkingAsPaid}
                onClick={(e) => {
                  e.stopPropagation()
                  onMarkAsPaid(invoice)
                }}
              >
                {isMarkingAsPaid ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <CircleCheck className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Log payment</TooltipContent>
          </Tooltip>
        ) : null}

        {canVoid ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={isScheduled ? 'Cancel scheduled invoice' : 'Void invoice'}
                onClick={(e) => {
                  e.stopPropagation()
                  onRequestVoid(invoice)
                }}
              >
                <Ban className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isScheduled ? 'Cancel invoice' : 'Void invoice'}</TooltipContent>
          </Tooltip>
        ) : null}

        {canRefund ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Refund invoice"
                onClick={(e) => {
                  e.stopPropagation()
                  onRequestRefund(invoice)
                }}
              >
                <Undo2 className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refund invoice</TooltipContent>
          </Tooltip>
        ) : null}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand(invoice.id)
              }}
            >
              {isExpanded ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{isExpanded ? 'Collapse' : 'Expand'}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}
